// Copyright 2023 Sorama B.V.

import { autoinject, BindingEngine, Disposable, observable } from "aurelia-framework";
import { MeasurementWorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog/MeasurementModuleCatalog";
import { FileToUpload } from "controls/file-dropbox/FileDropbox";
import { FileTree } from "controls/file-tree/FileTree";
import { NodeModel } from "controls/file-tree/TreeNode";
import { DateFilter, Filter } from "controls/filter-menu/FilterMenu";
import { GenericMeasurement, SpecialMeasurementType } from "datatypes/SoramaWebApi";
import { genericMeasurementToReportableMeasurement } from "datatypes/SpecialMeasurement";
import { FileDownloader } from "libraries/FileDownloader/FileDownloader";
import { ProgressIndicator } from "libraries/ProgressIndicator/ProgressIndicator";
import { IWorkflowModuleInstance } from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { ShellViewManager } from "modules/shell/App/ShellViewManager";
import { MeasurementRepository } from "repositories/MeasurementRepository";
import { CamIV64File, CamIV64FileType } from "services/Hardware/CamHTTP/CamIV64File";
import { CamIV64Helpers } from "services/Hardware/CamHTTP/CamIV64Helpers";
import { CamIV64Service } from "services/Hardware/CamHTTP/CamIV64Service";
import { ICamApi, SpecialMeasurementFileBase } from "services/Hardware/CamHTTP/ICamApi";
import { MeasurementStorageService } from "services/Measurement/MeasurementStorageService";
import { IProjectManagementService } from "services/Project/IProjectManagementService";
import { WorkflowModeService } from "services/WorkflowModes/WorkflowModeService";
import { SpecialMeasurementReportGenerator } from "../SpecialMeasurements/SpecialMeasurementReportGenerator/SpecialMeasurementReportGenerator";

export interface SpecialMeasurementDTOWithDownload extends GenericMeasurement {
    isDownloading: boolean;
}

@autoinject()
export class SpecialMeasurementsModule {
    private subscriptions: Disposable[] = [];

    private moduleInstance: IWorkflowModuleInstance;

    private fileTree: FileTree;

    private fileList: CamIV64File[] = [];
    private filteredFileList: CamIV64File[] = [];

    private specialMeasurementFilters: Filter<CamIV64File>[] = Object.values(SpecialMeasurementType).map((x) => {
        return {
            name: x.toLocaleString(),
            filter: (f: CamIV64File) =>
                f.fileType === CamIV64FileType.SpecialMeasurement && this.getSpecialMeasurementType(f) === x,
        };
    });

    private camIv64FileFilters: Filter<CamIV64File>[] = [
        { name: "Measurement", filter: (f: CamIV64File) => f.fileType === CamIV64FileType.Measurement },
        { name: "Image", filter: (f: CamIV64File) => f.fileType === CamIV64FileType.Image },
    ];

    private fileFilters: Filter<CamIV64File>[] = [...this.specialMeasurementFilters, ...this.camIv64FileFilters];

    private dateFilter: DateFilter<CamIV64File> = {
        getDate: (file: CamIV64File) => {
            const date = file.createDate;
            date?.setHours(0, 0, 0, 0);
            return date;
        },
    };

    private filteredSpecialMeasurements: SpecialMeasurementDTOWithDownload[] = [];
    @observable private selectedSpecialMeasurementId: string;

    private reportGenerator: SpecialMeasurementReportGenerator;

    constructor(
        private readonly projectManagementService: IProjectManagementService,
        private readonly measurementRepository: MeasurementRepository,
        private readonly measurementStorageService: MeasurementStorageService,
        private readonly shellviewManager: ShellViewManager,
        private readonly camApi: ICamApi,
        private readonly bindingEngine: BindingEngine,
        private readonly camIV64Service: CamIV64Service,
        private readonly shellViewManager: ShellViewManager,
        private readonly workflowModeService: WorkflowModeService
    ) {}

    async bind() {
        this.subscriptions = [
            this.bindingEngine
                .propertyObserver(this.camIV64Service, "selectedDeviceId")
                .subscribe(() => this.selectedDeviceIdChanged()),
            this.bindingEngine.propertyObserver(this, "filteredFileList").subscribe(() => this.fileTree.refresh()),
        ];
        if (this.isConnected) {
            this.selectedDeviceIdChanged();
        }
    }

    public unbind(): void {
        while (this.subscriptions.length !== 0) {
            this.subscriptions.pop().dispose();
        }
    }

    private get isConnected(): boolean {
        return this.camIV64Service.selectedDeviceId != "" && this.camIV64Service.selectedDeviceId != undefined;
    }

    private get showSpecialMeasurementList(): boolean {
        return (
            this.projectManagementService.currentProject.status.available &&
            this.projectManagementService.currentProject.item &&
            this.filteredSpecialMeasurements.length > 0
        );
    }

    private getSpecialMeasurementType(file: CamIV64File): SpecialMeasurementType {
        const keywords = Object.keys(SpecialMeasurementType);
        const keyword = keywords.find((keyword) => file.name.toUpperCase().includes(keyword));
        if (keyword) {
            return SpecialMeasurementType[keyword];
        }
    }

    private async selectedDeviceIdChanged() {
        this.fileList = await this.getAllFilesFromCAMiV64();
    }

    protected get selectedDeviceName(): string {
        return CamIV64Helpers.getDeviceName(this.camIV64Service.selectedDeviceId);
    }

    public openHardwareSelection() {
        this.shellViewManager.openRightSideBar(MeasurementWorkflowModuleCatalog.hardwareSelectionModule);
    }

    public async getAllFilesFromCAMiV64(): Promise<CamIV64File[]> {
        const files = await this.camApi.getCAMiV64Files();
        return files.map((file) => new CamIV64File(file.filePath, file.createDate));
    }

    public async refreshAndGetTreeRoot(): Promise<NodeModel | void> {
        const filePaths = this.filteredFileList.map((file) => file.filePath);
        return FileTree.createTreeFromPaths(filePaths);
    }

    public async activateModule(moduleInstance: IWorkflowModuleInstance): Promise<void> {
        this.moduleInstance = moduleInstance;
        await this.projectManagementService.getCurrentProject();
    }

    protected selectProject(): void {
        this.shellviewManager.openRightSideBar(MeasurementWorkflowModuleCatalog.ProjectManagementModule);
    }

    private async uploadSpecialMeasurement(data: SpecialMeasurementFileBase, file: NodeModel): Promise<void> {
        const projectId = this.projectManagementService.currentProject.item.id;

        // Removes the extension from the file
        data.fileName = file.nameAndExtension.split(".")[0];
        if (!data.name) {
            data.name = data.fileName;
        }

        await this.measurementRepository.createSpecialMeasurement(data, projectId);
    }

    public async importFile(event: {
        file: NodeModel;
        importProgressCallback: (progress: ProgressIndicator) => void;
    }): Promise<void> {
        if (!this.projectManagementService.currentProject.item) {
            throw new Error("Please select a project in order to import the file");
        }
        if (this.isSpecialMeasurement(event.file)) {
            const specialMeasurementData = await this.camApi.downloadSpecialMeasurementFile(
                event.file.path,
                event.importProgressCallback
            );
            await this.uploadSpecialMeasurement(specialMeasurementData, event.file);
        } else if (event.file.extension.toLowerCase() == "sor") {
            return this.importSORFile(event.file.path, event.importProgressCallback);
        } else if (event.file.extension.toLowerCase() == "sorx") {
            return this.importSorXFile(event.file.path);
        } else if (event.file.extension == "jpg" || event.file.extension == "jpeg" || event.file.extension == "png") {
            try {
                const imageBlob = await this.camApi.downloadMeasurementFile(
                    event.file.path,
                    event.importProgressCallback
                );
                const fileBlob = new FileToUpload(
                    event.file.path.split("/").pop(),
                    imageBlob,
                    "CAM iV64 measurement",
                    imageBlob.type,
                    () => {}
                );
                return this.uploadImage(fileBlob, event.importProgressCallback);
            } catch (e) {
                throw new Error(`Could not download the image: ${e.message}`);
            }
        }

        this.projectManagementService.refreshMeasurements();
    }

    public async deleteFile(file: NodeModel): Promise<void> {
        return this.camApi.deleteFileOrFolder(file.path);
    }

    public async downloadFile(event: {
        file: NodeModel;
        downloadProgressCallback: (progress: ProgressIndicator) => void;
        directly?: boolean;
    }): Promise<void | Blob> {
        if (this.isSpecialMeasurement(event.file)) {
            const data: SpecialMeasurementFileBase = await this.camApi.downloadSpecialMeasurementFile(event.file.path);
            data.fileName = event.file.name;
            if (!data.name) {
                data.name = data.fileName;
            }

            const type = MeasurementRepository.getSpecialMeasurementTypeFromData(data);
            const genericMeasurement = {
                name: event.file.name,
                specialMeasurementType: type,
                specialMeasurementInfo: data,
            } as GenericMeasurement;

            const report = genericMeasurementToReportableMeasurement(genericMeasurement);
            await this.reportGenerator.generatePdf(report);
        } else if (event.file.type === "SorX") {
            const measurementBlob = await this.camApi.downloadSorXMeasurementFile(
                event.file.path,
                event.downloadProgressCallback
            );
            FileDownloader.downloadBlobAsFile(
                measurementBlob,
                "application/octet-stream",
                // Download sorx as zip so the user is aware they can open it
                event.file.name + ".zip"
            );
        } else {
            const measurementBlob = await this.camApi.downloadMeasurementFile(
                event.file.path,
                event.downloadProgressCallback
            );
            if (!event.directly) return measurementBlob;
            FileDownloader.downloadBlobAsFile(measurementBlob, "application/octet-stream", event.file.nameAndExtension);
        }
    }

    private isSpecialMeasurement(file: NodeModel): boolean {
        const specialMeasurementTypeList = Object.keys(SpecialMeasurementType);
        return specialMeasurementTypeList.includes(file.type);
    }

    public async downloadFromCloud(specialMeasurement: SpecialMeasurementDTOWithDownload) {
        specialMeasurement.isDownloading = true;
        const cloudSpecialMeasurement = await this.measurementRepository.getMeasurement(specialMeasurement.id);

        const report = genericMeasurementToReportableMeasurement(cloudSpecialMeasurement);
        await this.reportGenerator.downloadPdf(report);
        specialMeasurement.isDownloading = false;
    }

    // Only used for testing with json files
    protected uploadFromFile = async (file: FileToUpload): Promise<void> => {
        const reader = new FileReader();
        reader.readAsText(file.data);
        reader.onloadend = () => {
            const data = JSON.parse(<string>reader.result);
            const type = MeasurementRepository.getSpecialMeasurementTypeFromData(data);
            const genericMeasurement = {
                name: file.name,
                specialMeasurementType: type,
                specialMeasurementInfo: data,
            } as GenericMeasurement;

            const report = genericMeasurementToReportableMeasurement(genericMeasurement);
            this.reportGenerator.downloadPdf(report);
        };
    };

    protected get isBuilding(): boolean {
        return this.workflowModeService.selectedWorkflowMode.isBuilding;
    }

    public async importSorXFile(
        filePath: string,
        importProgressCallback?: (progress: ProgressIndicator) => void
    ): Promise<void> {
        const progressCallback = typeof importProgressCallback === "function" ? importProgressCallback : undefined;

        const measurementBlob = await this.camApi.downloadSorXMeasurementFile(filePath, progressCallback).catch((e) => {
            throw new Error(`Download failed: ${e.message}`);
        });

        const fileName = filePath.split("/").pop() ?? filePath;
        const file = new FileToUpload(
            fileName,
            measurementBlob,
            "CAM iV64 measurement",
            "application/octet-stream",
            progressCallback ?? (() => {})
        );

        const projectId = this.projectManagementService.currentProject.item.id;

        try {
            await this.measurementStorageService.uploadSorxToCloud(file, projectId, progressCallback);
            this.projectManagementService.refreshMeasurements();
        } catch (e) {
            throw new Error(`Upload failed: ${(e as Error).message}`);
        }
    }

    public async importSORFile(
        filePath: string,
        importProgressCallback: (progress: ProgressIndicator) => void
    ): Promise<void> {
        const measurementBlob = await this.camApi
            .downloadMeasurementFile(filePath, importProgressCallback)
            .catch((e) => {
                throw new Error(`Download failed: ${e.message}`);
            });

        const file = new FileToUpload(
            filePath.split("/").pop(),
            measurementBlob,
            "CAM iV64 measurement",
            measurementBlob.type,
            () => {}
        );

        const projectId = this.projectManagementService.currentProject.item.id;
        const ownerId = this.projectManagementService.currentProject.item.ownerId;

        try {
            const genericMeasurement = await this.measurementStorageService.saveSorFileLocallyAndRegister(
                file,
                projectId,
                ownerId,
                importProgressCallback
            );
            this.projectManagementService.refreshMeasurements();
        } catch (e) {
            throw new Error(`Upload failed: ${e.message}`);
        }
    }

    protected uploadImage = async (
        file: FileToUpload,
        importProgressCallback: (progress: ProgressIndicator) => void
    ): Promise<void> => {
        const base64 = await file.dataAsBase64();
        const headerRegex = /data:.*;base64,/;
        const regexMatch = headerRegex.exec(base64);
        const data = regexMatch ? base64.substr(regexMatch[0].length) : base64;

        const cAMiV64Image = {
            name: file.name,
            description: file.description,
            data: data,
            mimeType: file.mimeType,
        };

        return this.measurementRepository
            .createCAMiV64Image(cAMiV64Image, importProgressCallback)
            .then(() => {
                this.projectManagementService.refreshMeasurements();
            })
            .catch((e) => {
                throw new Error(`Could not import the image file: ${e.message}`);
            });
    };
}
